import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
  runInAction,
} from "mobx";
import { DateTime } from "luxon";
import { GatedFeature, GatedFeatureConfig, PlanId } from "@/store/feature-gates/types";
import { type FeatureGateLevel, PaywallContext } from "@/domains/metrics/tracked-event";
import { buildFeatureGateConfigFromData, getPeriodWindow } from "@/store/feature-gates/utils";
import { GATED_FEATURE_DEFAULTS, UNKNOWN_GATE_FALLBACK } from "@/store/feature-gates/defaults";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { liveQuery, Subscription } from "dexie";

export class AppStoreFeatureGatesStore extends AppSubStore {
  upsellBannerVisible: boolean = false;
  upsellBannerExceeded: boolean = false;

  private noteCreationCounterSubscription?: Subscription;
  private chatMessageCounterSubscription?: Subscription;
  private pdfPagesProcessedCounterSubscription?: Subscription;
  private _noteCreationCount: number = 0;
  private _chatMessageCount: number = 0;
  private _pdfPagesProcessedCount: number = 0;
  private _currentPeriodStart: DateTime = DateTime.local().startOf("month");

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      AppStoreFeatureGatesStore,
      | "getGateConfig"
      | "subscribeToNoteCreationCounter"
      | "unsubscribeFromNoteCreationCounter"
      | "subscribeToChatMessageCounter"
      | "unsubscribeFromChatMessageCounter"
      | "subscribeToPdfPagesProcessedCounter"
      | "unsubscribeFromPdfPagesProcessedCounter"
      | "noteCreationCounterSubscription"
      | "chatMessageCounterSubscription"
      | "pdfPagesProcessedCounterSubscription"
      | "_noteCreationCount"
      | "_chatMessageCount"
      | "_pdfPagesProcessedCount"
      | "_currentPeriodStart"
    >(this, {
      useInitializeAppStoreEffects: false,
      openNoteCreationUpsell: false,
      openChatMessageUpsell: false,
      checkGate: false,
      openUsageLimitUpsell: action,
      closeUpsell: action,
      getGateConfig: false,
      upsellBannerVisible: observable,
      upsellBannerExceeded: observable,
      refreshUpsellBanner: action,
      noteCreationCount: computed,
      chatMessageCount: computed,
      pdfPagesProcessedCount: computed,
      currentPeriodStart: computed,
      subscribeToNoteCreationCounter: action,
      unsubscribeFromNoteCreationCounter: action,
      subscribeToChatMessageCounter: action,
      unsubscribeFromChatMessageCounter: action,
      subscribeToPdfPagesProcessedCounter: action,
      unsubscribeFromPdfPagesProcessedCounter: action,
      noteCreationCounterSubscription: false,
      chatMessageCounterSubscription: false,
      pdfPagesProcessedCounterSubscription: false,
      _noteCreationCount: observable,
      _chatMessageCount: observable,
      _pdfPagesProcessedCount: observable,
      _currentPeriodStart: observable,
    });

    onBecomeObserved(this, "noteCreationCount", () => this.subscribeToNoteCreationCounter());
    onBecomeUnobserved(this, "noteCreationCount", () => this.unsubscribeFromNoteCreationCounter());
    onBecomeObserved(this, "chatMessageCount", () => this.subscribeToChatMessageCounter());
    onBecomeUnobserved(this, "chatMessageCount", () => this.unsubscribeFromChatMessageCounter());
    onBecomeObserved(this, "pdfPagesProcessedCount", () =>
      this.subscribeToPdfPagesProcessedCounter()
    );
    onBecomeUnobserved(this, "pdfPagesProcessedCount", () =>
      this.unsubscribeFromPdfPagesProcessedCounter()
    );
  }

  get currentPeriodStart(): DateTime {
    return this._currentPeriodStart;
  }

  get noteCreationCount(): number {
    return this._noteCreationCount;
  }

  get chatMessageCount(): number {
    return this._chatMessageCount;
  }

  get pdfPagesProcessedCount(): number {
    return this._pdfPagesProcessedCount;
  }

  subscribeToNoteCreationCounter = () => {
    this.unsubscribeFromNoteCreationCounter();
    const periodStart = DateTime.local().startOf("month");
    this._currentPeriodStart = periodStart;

    this.noteCreationCounterSubscription = liveQuery(async () => {
      // liveQuery tracks Dexie queries made within async functions, so calling
      // countMyNotesCreatedSince works correctly and will react to database changes
      return await this.store.notes.countMyNotesCreatedSince(periodStart);
    }).subscribe({
      next: count => {
        runInAction(() => {
          this._noteCreationCount = count;
        });
      },
    });
  };

  unsubscribeFromNoteCreationCounter = () => {
    this.noteCreationCounterSubscription?.unsubscribe();
    this.noteCreationCounterSubscription = undefined;
  };

  subscribeToChatMessageCounter = () => {
    this.unsubscribeFromChatMessageCounter();
    const periodStart = DateTime.local().startOf("month");

    this.chatMessageCounterSubscription = liveQuery(async () => {
      // liveQuery tracks Dexie queries made within async functions, so calling
      // countMyChatMessagesSentSince works correctly and will react to database changes
      return await this.store.sessionEvents.countMyChatMessagesSentSince(periodStart);
    }).subscribe({
      next: count => {
        runInAction(() => {
          this._chatMessageCount = count;
        });
      },
    });
  };

  unsubscribeFromChatMessageCounter = () => {
    this.chatMessageCounterSubscription?.unsubscribe();
    this.chatMessageCounterSubscription = undefined;
  };

  private subscribeToPdfPagesProcessedCounter = () => {
    this.unsubscribeFromPdfPagesProcessedCounter();
    const periodStart = DateTime.local().startOf("month");

    this.pdfPagesProcessedCounterSubscription = liveQuery(async () => {
      return await this.store.spaceAccountNoteAttachments.countPdfPagesProcessedSince(periodStart);
    }).subscribe({
      next: count => {
        runInAction(() => {
          this._pdfPagesProcessedCount = count;
        });
      },
    });
  };

  private unsubscribeFromPdfPagesProcessedCounter = () => {
    this.pdfPagesProcessedCounterSubscription?.unsubscribe();
    this.pdfPagesProcessedCounterSubscription = undefined;
  };

  useInitializeAppStoreEffects() {
    useEffectOnMount(() => {
      this.refreshUpsellBanner();
      const reactionDisposers = [
        reaction(
          () => ({
            queuesAreActive: this.store.notes.queuesAreActive,
            noteCreationCount: this.noteCreationCount,
            chatMessageCount: this.chatMessageCount,
            pdfPagesProcessedCount: this.pdfPagesProcessedCount,
            tier: this.store.subscriptions.personalSubscription?.tier,
          }),
          () => this.refreshUpsellBanner()
        ),
      ];
      return () => reactionDisposers.forEach(f => f());
    });
  }

  private getGateConfig(gateId: GatedFeature): GatedFeatureConfig {
    const storeConfig = this.store.platformInfo?.getFeatureGateConfig?.(gateId);
    if (!storeConfig) return GATED_FEATURE_DEFAULTS[gateId] ?? UNKNOWN_GATE_FALLBACK;

    return buildFeatureGateConfigFromData(storeConfig, gateId);
  }

  checkGate(gateId: GatedFeature): {
    allowed: boolean;
    used: number;
    limit: number;
    warnAt: number | null;
  } {
    const subscription = this.store.subscriptions.personalSubscription;
    const planId: PlanId = (subscription?.tier as PlanId) || PlanId.Paid;
    const { limits, period, counter } = this.getGateConfig(gateId);
    const planLimit = limits[planId];

    const { periodStart, periodEnd } = getPeriodWindow(period, DateTime.local());
    const used = counter({
      store: this.store,
      periodStart,
      periodEnd,
    });

    if (planLimit === null) {
      // no plan limit means unlimited
      return { allowed: true, used, limit: Infinity, warnAt: null };
    }

    return { allowed: used < planLimit, used, limit: planLimit, warnAt: limits.warning };
  }

  openNoteCreationUpsell(params: { used: number; limit: number }) {
    // Delegate to generic upsell
    this.openUsageLimitUpsell({
      ...params,
      feature: GatedFeature.NoteCreate,
      level: "exceeded",
    });
  }

  openChatMessageUpsell(params: { used: number; limit: number }) {
    // Delegate to generic upsell
    this.openUsageLimitUpsell({
      ...params,
      feature: GatedFeature.ChatMessageSend,
      level: "exceeded",
    });
  }

  openUsageLimitUpsell(params: {
    used: number;
    limit: number;
    feature: GatedFeature | undefined;
    context?: PaywallContext;
    level?: FeatureGateLevel;
  }) {
    this.store.subscriptions.openUsageLimitUpsell({
      ...params,
      level: params.level ?? "exceeded",
    });
  }

  closeUpsell = () => {
    this.store.subscriptions.closeUpsell();
  };

  /**
   * Computes and caches the banner visibility and exceeded state for note creation usage.
   */
  refreshUpsellBanner() {
    const notesResult = this.checkGate(GatedFeature.NoteCreate);
    const chatResult = this.checkGate(GatedFeature.ChatMessageSend);

    const isPdfPaywallEnabled =
      this.store.spaceAccountFeatureFlags.getFlagStatus("pdf-paywall") ?? false;
    const pdfResult = isPdfPaywallEnabled
      ? this.checkGate(GatedFeature.ContentUnderstandingPdfPage)
      : null;

    const notesWarn = Boolean(notesResult.warnAt && notesResult.used >= notesResult.warnAt);
    const chatWarn = Boolean(chatResult.warnAt && chatResult.used >= chatResult.warnAt);
    const pdfWarn = Boolean(pdfResult?.warnAt && pdfResult.used >= pdfResult.warnAt);

    const notesExceeded = notesResult.used >= notesResult.limit;
    const chatExceeded = chatResult.used >= chatResult.limit;
    const pdfExceeded = pdfResult ? pdfResult.used >= pdfResult.limit : false;

    const isVisible =
      notesWarn || chatWarn || pdfWarn || notesExceeded || chatExceeded || pdfExceeded;

    runInAction(() => {
      this.upsellBannerVisible = isVisible;
      this.upsellBannerExceeded = notesExceeded || chatExceeded || pdfExceeded;
    });
    if (!isVisible) {
      this.store.subscriptions.closeUpsell();
    }
  }
}
